import { Stack, TableCell, TableRow, Typography } from '@mui/material';
import EmptyIconButton from 'components/ui/buttons/EmptyIconButton';
import IconButtonStyled from 'components/ui/buttons/IconButtonStyled';
import MenuButton from 'components/ui/buttons/MenuButton';
import { DownloadIcon } from 'components/ui/icons';
import PopupAction from 'components/ui/popups/PopupAction';
import { TABLE_ROW_SELECTABLE_CLASS_NAME } from 'configs/layout';
import { MODEL_PERMISSION } from 'configs/permissions/modelPermissions';
import useDI from 'hooks/useDI';
import useLocales from 'hooks/useLocales';
import useStableNavigate from 'hooks/useStableNavigate';
import { useMemo, useState } from 'react';
import { RoutingServiceDashboard } from 'services/routing/RoutingService';
import { useSelector } from 'storage';
import FileUtils from 'utils/File';

type Props = {
  documentTemplate: DocumentTemplate;
};

export default function DocumentTemplateListItem({ documentTemplate }: Props) {
  const { translate } = useLocales();
  const { permissions, currentUser } = useSelector((state) => state.auth);
  const [isDeletePopupOpened, setIsDeletePopupOpened] = useState(false);
  const { statefulUtils, storageActions, apiService } = useDI();
  const navigate = useStableNavigate();

  const menuOptions = useMemo(() => {
    const menuOptions: MenuItemData[] = [];
    if (permissions.can(MODEL_PERMISSION.CAN_UPDATE_DOCUMENT_TEMPLATE)) {
      menuOptions.push({
        title: translate('pages.documentTemplate.actions.update'),
        handler: () => {
          navigate(RoutingServiceDashboard.modelsDirectory.documentTemplate.getEditPath({ documentTemplateId: documentTemplate.id }));
        },
      });
    }
    if (permissions.can(MODEL_PERMISSION.CAN_DELETE_DOCUMENT_TEMPLATE)) {
      menuOptions.push({
        title: translate('pages.documentTemplate.actions.delete'),
        handler: () => {
          setIsDeletePopupOpened(true);
        },
      });
    }
    return menuOptions;
  }, [permissions, documentTemplate.id, translate, navigate]);

  const deleteHandler = async () => {
    try {
      if (!currentUser?.tenant.id) {
        throw new Error(translate('errors.tenantNotFound'));
      }
      await storageActions.models.documentTemplate.delete(documentTemplate.id, undefined, { fileId: documentTemplate.file.id });
      await storageActions.models.documentTemplate.getList({ tenantId: currentUser.tenant.id });
    } catch (error) {
      throw new Error(translate('errors.documentTemplateDeleteError'));
    }
  };

  const downloadHandler = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const fileData = await apiService.subEntity.file.getActualFileData(documentTemplate.file);
      const blob = await apiService.subEntity.file.getFileBlob(fileData);
      FileUtils.downloadBlob(blob, fileData.name);
    } catch (error) {
      throw new Error(translate('errors.documentTemplateDownloadError'));
    }
  };

  return (
    <>
      <TableRow key={documentTemplate.id} className={TABLE_ROW_SELECTABLE_CLASS_NAME}>
        <TableCell>
          <Typography variant="body2">{documentTemplate.name}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{statefulUtils.date.getDateStr(documentTemplate.createdAt, 'long')}</Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" justifyContent="flex-end">
            <IconButtonStyled title={translate('pages.orderDocumentList.downloadTemplate')} onClick={downloadHandler}>
              <DownloadIcon />
            </IconButtonStyled>
            {menuOptions.length > 0 ? (
              <MenuButton options={menuOptions} title={translate('pages.documentTemplate.tooltipTitle')} />
            ) : (
              <EmptyIconButton />
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <PopupAction
        title={translate('pages.orderDocumentInfo.deleteTitle')}
        isOpened={isDeletePopupOpened}
        actionHandler={deleteHandler}
        closeHandler={() => setIsDeletePopupOpened(false)}
      />
    </>
  );
}
